import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { FormProvider, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useSnackbar } from 'notistack';

import { Box, Button, Card, Divider, Stack, Typography } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';

import { useConfirmDialog } from 'contexts/ConfirmDialogContext';
import { DrawerContent } from 'components/common/drawer/DrawerContent';
import { FormSelectField } from 'components/common/form/Form';
import { loadQuoteSchema } from 'validation/form/marketplace';
import { listSavedQuotes, loadQuote, deleteSavedQuote } from 'store/slices/marketplace';
import { useAsyncDispatch } from 'hooks/useAsyncDispatch';

export const LoadQuote = (props) => {
  const { close } = props;
  const dispatch = useDispatch();
  const dispatchAction = useAsyncDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const { openConfirmDialog } = useConfirmDialog();

  const [currentQuote, setCurrentQuote] = useState(null);

  const loading = useSelector((state) => state.marketplace.loadQuoteForm.loading);
  const selectedQuoteSet = useSelector((state) => state.marketplace.selectedQuoteSet);
  const savedQuotes = useSelector((state) => state.marketplace.savedQuotes.value);
  const loadingSavedQuotes = useSelector((state) => state.marketplace.savedQuotes.loading);

  const methods = useForm({
    resolver: yupResolver(loadQuoteSchema),
    defaultValues: {
      quote: '',
    },
  });

  const selectedQuote = methods.watch('quote');

  const handleSave = methods.handleSubmit((data) => {
    dispatchAction(loadQuote({ quoteSetId: selectedQuoteSet?.id, quoteId: data?.quote }), {
      onSuccess: () => {
        enqueueSnackbar('Quote has been loaded');
        close();
      },
    });
  });

  const handleDeleteSavedQuote = () => {
    if (!currentQuote) return;
    openConfirmDialog({
      title: 'Delete Saved Quote',
      text: `This will delete the saved quote.`,
      handler: () => {
        dispatchAction(deleteSavedQuote({ quoteId: currentQuote.id }), {
          onSuccess: () => {
            enqueueSnackbar(`Saved quote has been deleted`);
            close();
          },
          errorFallback: 'Unable to delete saved quote',
        });
      },
    });
  };

  useEffect(() => {
    dispatch(listSavedQuotes());
  }, [dispatch]);

  useEffect(() => {
    const selected = savedQuotes.find((quote) => quote.id === selectedQuote);
    setCurrentQuote(selected);
  }, [selectedQuote, savedQuotes]);

  return (
    <DrawerContent>
      <DrawerContent.Header
        title="Load Quote"
        loading={loadingSavedQuotes}
      />
      <DrawerContent.Wrapper>
        <DrawerContent.Content>
          <Stack spacing={3}>
            <FormProvider {...methods}>
              <FormSelectField
                name="quote"
                label="Quote"
                control={methods.control}
                options={savedQuotes}
                valueName="id"
                optionName="name"
              />
            </FormProvider>
            {currentQuote && (
              <>
                <Divider />
                <Card
                  variant="outlined"
                  sx={{
                    width: '100%',
                    height: 'fit-content',
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden',
                    borderColor: 'divider',
                  }}
                >
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: (theme) => theme.palette.grey[200],
                      borderBottom: 1,
                      borderColor: 'divider',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        fontWeight: 500,
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mr: 1.5,
                      }}
                    >
                      {currentQuote.name}
                    </Typography>
                    <Button
                      size="small"
                      color="error"
                      startIcon={<DeleteOutlineIcon />}
                      onClick={handleDeleteSavedQuote}
                      sx={{ textTransform: 'none', fontWeight: 600 }}
                    >
                      Delete
                    </Button>
                  </Box>

                  <Box sx={{ p: 0 }}>
                    <Stack
                      spacing={0}
                      divider={<Divider />}
                    >
                      {currentQuote?.items?.length === 0 ? (
                        <Box sx={{ p: 3, textAlign: 'center' }}>
                          <Typography
                            variant="body2"
                            color="text.secondary"
                          >
                            No products yet
                          </Typography>
                        </Box>
                      ) : (
                        currentQuote.items.map((product, index) => (
                          <Box
                            key={index}
                            sx={{
                              p: 1.5,
                              px: 2,
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center',
                            }}
                          >
                            <Box sx={{ pr: 2, flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="body2"
                                sx={{
                                  mb: 0.5,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {product.product_item_description}
                              </Typography>
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{
                                  display: 'block',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {product?.supplier?.name}
                              </Typography>
                            </Box>
                            <Typography variant="body2">${product.price.toFixed(2)}</Typography>
                          </Box>
                        ))
                      )}
                    </Stack>
                  </Box>
                </Card>
              </>
            )}
          </Stack>
        </DrawerContent.Content>
        <DrawerContent.ActionButtons
          submitButtonText="Load"
          submitButtonLoading={loading}
          submit={handleSave}
          close={close}
        />
      </DrawerContent.Wrapper>
    </DrawerContent>
  );
};
